import React, { useEffect, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { db } from "@/utils/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import Spinner from "@/components/Spinner";
import getConstants from "@/scripts/getConstants";
import updateConstants from "@/scripts/updateConstants";
import { TrashIcon, QuestionMarkCircledIcon } from "@radix-ui/react-icons";

const formSchema = z.object({
  buyer_matchmaking_day: z
    .number()
    .min(1, "Must be at least 1")
    .max(365, "Cannot exceed 365")
    .or(z.string().regex(/^\d+$/, "Must be a number").transform(Number)),
  seller_matchmaking_day: z
    .number()
    .min(1, "Must be at least 1")
    .max(365, "Cannot exceed 365")
    .or(z.string().regex(/^\d+$/, "Must be a number").transform(Number)),
  buyer_visible_request_day: z
    .number()
    .min(1, "Must be at least 1")
    .max(365, "Cannot exceed 365")
    .or(z.string().regex(/^\d+$/, "Must be a number").transform(Number)),
  display_pincode: z.boolean(),
  display_quantity: z.boolean(),
  enable_form_matchmaking: z.boolean(),
  matching_mode: z.string()
});

const ToolTip = ({ content }) => {
  return (
    <TooltipProvider key={content}>
      <Tooltip>
        <TooltipTrigger asChild>
          <QuestionMarkCircledIcon />
        </TooltipTrigger>
        <TooltipContent>
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

function Settings() {
  const [mode, setMode] = useState("Settings");
  const [loading, setLoading] = useState(false);
  // Constants
  const [forms, setForms] = useState([]);
  const [grades, setGrades] = useState([]);
  const [newForm, setNewForm] = useState("");
  const [newGrade, setNewGrade] = useState("");

  const { reset, ...form } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      buyer_matchmaking_day: "",
      seller_matchmaking_day: "",
      buyer_visible_request_day: "",
      display_pincode: false,
      display_quantity: false,
      enable_form_matchmaking: false,
      matching_mode: "request"
    },
  });

  const getData = async () => {
    try {
      setLoading(true);
      // Reference to the specific document
      const userDocRef = doc(db, "settings", "default");
      // Get the document data
      const docSnap = await getDoc(userDocRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        reset({
          buyer_matchmaking_day: data.buyer_matchmaking_day || "",
          seller_matchmaking_day: data.seller_matchmaking_day || "",
          buyer_visible_request_day: data.buyer_visible_request_day || "",
          display_pincode: data.display_pincode || false,
          display_quantity: data.display_quantity || false,
          enable_form_matchmaking: data.enable_form_matchmaking || false,
          matching_mode: data.matching_mode || "request"
        });
      }
    } catch (error) {
      console.error("Error fetching document:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      // Reference to the specific document
      const userDocRef = doc(db, "settings", "default");
      // Update the document with the provided data
      await updateDoc(userDocRef, values);
      console.log("Settings updated successfully.");
    } catch (error) {
      console.error("Error updating settings:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchConstants = async () => {
    try {
      setLoading(true);
      const { forms, grades } = await getConstants();
      // Set values
      setForms(forms);
      setGrades(grades);
    } catch (err) {
      console.log("Unable to fetch constants", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGrade = async () => {
    if (newGrade.trim()) {
      await updateConstants("add", newGrade.trim(), "grades");
      setGrades((prev) => [...prev, newGrade.trim()]);
      setNewGrade("");
    }
  };

  const handleRemoveGrade = async (grade) => {
    await updateConstants("remove", grade, "grades");
    setGrades((prev) => prev.filter((g) => g !== grade));
  };

  const handleAddForm = async () => {
    if (newForm.trim()) {
      await updateConstants("add", newForm.trim(), "forms");
      setForms((prev) => [...prev, newForm.trim()]);
      setNewForm("");
    }
  };

  const handleRemoveForm = async (form) => {
    await updateConstants("remove", form, "forms");
    setForms((prev) => prev.filter((f) => f !== form));
  };

  useEffect(() => {
    getData();
    fetchConstants();
  }, []);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper">
      <div className="flex gap-5">
        <div className="cursor-pointer" onClick={() => setMode("Settings")}>
          <h1 className="text-sm font-bold text-green ml-0.5 w-[60px]">
            Settings
          </h1>
          {mode === "Settings" && <div className="highlight !w-[60px]" />}
        </div>

        <div className="cursor-pointer" onClick={() => setMode("Constants")}>
          <h1 className="text-sm font-bold text-green ml-0.5">
            Update Grades & Forms
          </h1>
          {mode === "Constants" && <div className="highlight !w-[163px]" />}
        </div>
      </div>

      {mode === "Settings" && (
        <Form {...form}>
          <form
            className="space-y-4 p-3"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <p className="mt-3 text-sm text-muted mobileMargin">
              Configure your application settings below.
            </p>

            {/* Display Quantity */}
            <FormField
              control={form.control}
              name="display_quantity"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between items-center">
                    <FormLabel>
                      Display Quantity{" "}
                      <span className="font-semibold">
                        : {field?.value ? "Enabled" : "Disabled"}
                      </span>
                    </FormLabel>
                    <ToolTip content="Enable or disable the display of quantity on listings." />
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Display Pincode */}
            <FormField
              control={form.control}
              name="display_pincode"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between items-center">
                    <FormLabel>
                      Display Pincode, Location{" "}
                      <span className="font-semibold">
                        : {field?.value ? "Enabled" : "Disabled"}
                      </span>
                    </FormLabel>
                    <ToolTip content="Enable or disable the display of pincode and location on listings." />
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Enable Form Matchmaking */}
            <FormField
              control={form.control}
              name="enable_form_matchmaking"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between items-center">
                     <FormLabel>
                      Enable Form Matchmaking{" "}
                      <span className="font-semibold">
                        : {field?.value ? "Enabled" : "Disabled"}
                      </span>
                    </FormLabel>
                    <ToolTip content="Enable or disable the forms while matchmaking on listings." />
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <hr />

            <FormField
              control={form.control}
              name="matching_mode"
              render={({ field }) => {
                const isRequestMode = field.value === "request";
              
                return (
                  <FormItem>
                    <div className="flex justify-between items-center">
                      <FormLabel>
                        Matchmaking Mode{" "}
                        <span className="font-semibold">
                          : {isRequestMode ? "Request-based" : "Preference-based"}
                        </span>
                      </FormLabel>
                    </div>
                
                    <FormControl>
                      <Switch
                        checked={isRequestMode}
                        onCheckedChange={(checked) =>
                          field.onChange(checked ? "request" : "preference")
                        }
                        className="mt-1"
                      />
                    </FormControl>
                      
                    {/* Detailed Note */}
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {isRequestMode ? (
                        <>
                          <strong>Request-based Matching:</strong> Both buyer and seller must
                          create requests and get them approved. The system only matches
                          approved requests with each other based on request details like
                          grade, and form (optional). For example, if a buyer submits a
                          PVC request and it gets approved, and a seller also submits a PVC
                          request which is approved, then they are matched. Matching only
                          works within a defined time window (e.g. 360 days). If either side
                          is not approved, no match will happen.
                        </>
                      ) : (
                        <>
                          <strong>Preference-based Matching:</strong> Only one side (buyer or
                          seller) needs to create and get a request approved. The system then
                          matches it with users from the opposite side based on their saved
                          preferences (set during account creation or earlier), even if they
                          have not created any request. For example, if a buyer’s PVC request
                          is approved, the system will match it with all sellers who have PVC
                          in their preferences. There is no time restriction, and this mode
                          enables faster and broader matchmaking.
                        </>
                      )}
                    </p>
                    
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <hr />

            <FormField
              control={form.control}
              name="buyer_visible_request_day"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between items-center">
                    <FormLabel>Buyer Visible Seller Requests Days</FormLabel>
                    <ToolTip content=" Number of days to look back for displaying previous seller requests to buyers. This helps buyers see relevant historical data." />
                  </div>
                  <FormControl>
                    <Input
                      placeholder="Enter days (e.g. 10)"
                      {...field}
                      type="number"
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <hr />

            {/* Bulk Notifications */}
            <FormField
              control={form.control}
              name="buyer_matchmaking_day"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between items-center">
                    <FormLabel>Buyer Matchmaking Days</FormLabel>
                    <ToolTip content="Number of days to look back for matching products for buyers." />
                  </div>
                  <FormControl>
                    <Input
                      placeholder="Enter days (e.g. 10)"
                      {...field}
                      type="number"
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Seller Listing Limit */}
            <FormField
              control={form.control}
              name="seller_matchmaking_day"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between items-center">
                    <FormLabel>Seller Matchmaking Days</FormLabel>
                    <ToolTip content="Number of days to look back for matching products for sellers." />
                  </div>
                  <FormControl>
                    <Input
                      placeholder="Enter days (e.g. 10)"
                      {...field}
                      type="number"
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <div className="text-right">
              <Button
                type="submit"
                className="w-[200px] bg-green mobileInputWrapper mt-2"
              >
                Save Settings
              </Button>
            </div>
          </form>
        </Form>
      )}

      {mode === "Constants" && (
        <div className="space-y-4 p-3">
          <h2 className="text-sm font-bold">Grades</h2>

          <ul className="h-[280px] overflow-scroll">
            {grades.map((grade, index) => (
              <li
                key={index}
                className="text-sm text-muted mb-1 flex justify-between items-center gap-3 mr-3"
              >
                <Input value={`${index + 1}. ${grade}`} disabled />
                <Button onClick={() => handleRemoveGrade(grade)}>
                  <TrashIcon />
                </Button>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3 mr-3">
            <Input
              type="text"
              value={newGrade}
              placeholder="Add new grade"
              onChange={(e) => setNewGrade(e.target.value)}
              className="mt-1"
            />
            <Button onClick={handleAddGrade}>Add Grade</Button>
          </div>

          <h2 className="text-sm font-bold">Forms</h2>

          <ul className="h-[280px] overflow-scroll">
            {forms.map((form, index) => (
              <li
                key={index}
                className="text-sm text-muted mb-1 flex justify-between items-center gap-3 mr-3"
              >
                <Input value={`${index + 1}. ${form}`} disabled />
                <Button onClick={() => handleRemoveForm(form)}>
                  <TrashIcon />
                </Button>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3 mr-3">
            <Input
              type="text"
              value={newForm}
              onChange={(e) => setNewForm(e.target.value)}
              placeholder="Add new form"
              className="mt-1"
            />
            <Button onClick={handleAddForm}>Add Form</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
